import styled from 'styled-components';
import {
  H3,
  Icon,
  Logo,
  Modal,
  Overflow,
  Skeleton,
  Text,
} from 'next/components/ui';
import { easing } from 'next/components/ui/lib';
import InstallButton from './InstallButton';
import TrialMeter from './TrialMeter';

const SMALL_ICONS = new Set(['comment-alt']);

/**
 * Styled Navigation components
 */
export const Wrapper = styled.div`
  background-color: var(--white);
  border-right: 1px solid var(--nav-border);
  bottom: 0;
  display: flex;
  flex-direction: column;
  left: 0;
  position: fixed;
  top: 0;
  width: var(--nav-width);
  z-index: 9999;
`;

export const Header = styled.header`
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 20px 0;

  ${Logo} {
    width: 20px;
  }
`;

// TODO: Consider using @radix-ui/react-scroll-area
export const Scrollable = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-y: auto;
`;

export const Links = styled.nav`
  flex-grow: 1;
`;

export const Group = styled.ul`
  padding: 12px 0;
`;

// TODO: Consider using @radix-ui/react-separator
export const Separator = styled.hr`
  border-top: 1px solid var(--ui-vertical-separator);
  border-bottom: 0;
`;

export const CallToActions = styled.div`
  display: flex;
  flex-direction: column;

  > ${InstallButton}, > ${TrialMeter} {
    margin: 0 24px 24px;
  }
`;

/**
 * Styled NavItem components
 */
export const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-right: 12px;
  width: 20px;
`;

export const ItemIcon = styled(Icon)`
  color: var(--text-secondary);
  font-size: var(--regular);

  ${({ icon }) =>
    SMALL_ICONS.has(icon) &&
    `
    font-size: var(--small) ;
  `}
`;

/**
 * Styled AccountSwitcher components
 */
export const AccountsModal = styled(Modal)`
  --account-modal-bg: var(--noreaster);
  --account-bg: var(--white);
  --account-border: var(--myst);
  --account-active-border: var(--primary);
  --account-disabled: var(--disabled);

  align-items: center;
  background: var(--account-modal-bg);
  display: flex;
  flex-direction: column;
`;

export const Accounts = styled(Overflow).attrs({
  'aria-live': 'polite',
})`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 48px 16px;
  width: 100%;
`;

export const Account = styled.div.attrs({ role: 'button' })`
  align-items: center;
  background: var(--account-bg);
  border: 1px solid var(--account-border);
  border-radius: 6px;
  box-shadow: none;
  display: flex;
  height: 64px;
  justify-content: space-between;
  max-width: 600px;
  padding: 12px 16px;
  transition: ${easing('border', 'box-shadow')};
  width: 100%;

  :hover {
    border-color: var(--account-active-border);
  }

  & + & {
    margin-top: 16px;
  }

  ${({ $selected }) =>
    $selected &&
    `
      border-color: var(--account-active-border);
      border-width: 2px;
      box-shadow: var(--level-2);
      transition: none;
  `}

  ${({ disabled }) =>
    disabled &&
    `
      ${H3}, ${Text} {
        color: var(--account-disabled);
      }
  `}
`;

export const AccountLoading = styled(Skeleton)`
  border: 1px solid var(--account-border);
  border-radius: 6px;
  height: 64px;
  max-width: 600px;
  width: 100%;

  & + & {
    margin-top: 16px;
  }
`;
